import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { customerService, CustomerTagPreferences } from '../services/customer';
import { ArrowLeftIcon, HeartIcon, TagIcon } from '@heroicons/react/24/outline';

const COLORS = [
  '#3B82F6', // blue
  '#EF4444', // red
  '#10B981', // green
  '#F59E0B', // amber
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#14B8A6', // teal
  '#F97316', // orange
  '#6366F1', // indigo
  '#84CC16', // lime
];

const CustomerPreferenceReport: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const [data, setData] = useState<CustomerTagPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCustomerPreferences();
  }, [customerId]);

  const loadCustomerPreferences = async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await customerService.getCustomerTagPreferences(customerId);
      setData(response);
    } catch (err: any) {
      console.error('Failed to load customer preferences:', err);
      setError(err.response?.data?.error || 'Failed to load customer preferences');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center text-gray-500">No data available</div>
      </div>
    );
  }

  // Prepare data for pie chart
  const chartData = data.tagPreferences.map((tag, index) => ({
    name: tag.name,
    value: tag.count,
    color: COLORS[index % COLORS.length]
  }));

  // Calculate percentage for each tag
  const totalCount = data.tagPreferences.reduce((sum, tag) => sum + tag.count, 0);

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/customers"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Customers
        </Link>
        <div className="mt-2">
          <h1 className="text-2xl font-semibold text-gray-900">
            {data.customer.name}'s Preferences
          </h1>
          <p className="mt-1 text-sm text-gray-600">{data.customer.email}</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <HeartIcon className="h-8 w-8 text-red-500 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-500">Total Liked Dresses</p>
              <p className="text-3xl font-semibold text-gray-900">{data.totalLikedDresses}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <TagIcon className="h-8 w-8 text-blue-500 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-500">Unique Tags</p>
              <p className="text-3xl font-semibold text-gray-900">{data.tagPreferences.length}</p>
            </div>
          </div>
        </div>
      </div>

      {data.totalLikedDresses === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <HeartIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">This customer hasn't liked any dresses yet.</p>
        </div>
      ) : (
        <>
          {/* Tag Preferences Chart and Table */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Tag Preferences</h2>

            {data.tagPreferences.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Pie Chart */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-4 text-center">Tag Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry: any) => `${entry.name} (${(entry.percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Tag List */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Popularity Ranking</h3>
                  <div className="space-y-3">
                    {data.tagPreferences.map((tag, index) => {
                      const percentage = ((tag.count / totalCount) * 100).toFixed(1);
                      return (
                        <div key={tag.categoryId} className="flex items-center">
                          <div
                            className="w-4 h-4 rounded mr-3"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-900">{tag.name}</span>
                              <span className="text-sm text-gray-500">
                                {tag.count} {tag.count === 1 ? 'dress' : 'dresses'} ({percentage}%)
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full"
                                style={{
                                  width: `${percentage}%`,
                                  backgroundColor: COLORS[index % COLORS.length]
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500">No tag data available</p>
            )}
          </div>

          {/* Liked Dresses Gallery */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Liked Dresses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.likedDresses.map((like) => (
                <div key={like.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  {like.dress.thumbnailUrl && (
                    <div className="aspect-w-3 aspect-h-4 bg-gray-100">
                      <img
                        src={like.dress.thumbnailUrl}
                        alt={like.dress.name}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-1">{like.dress.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{like.dress.designer.name}</p>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {like.dress.dressCategories.map((category) => (
                        <span
                          key={category.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {category.displayName}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400">
                      Liked {new Date(like.likedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerPreferenceReport;
